import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Pagination from 'react-responsive-pagination';
import 'react-responsive-pagination/themes/classic.css';
import AppLayout from '../components/AppLayout';
import DeficiencyFormSlider from '../components/DeficiencyFormSlider';
import DeficiencyDetailsSlider from '../components/DeficiencyDetailsSlider';
import ExportDeficienciesModal from '../components/ExportDeficienciesModal';
import PrintDeficienciesModal, { PrintOptions } from '../components/PrintDeficienciesModal';
import { AlertTriangle, Plus, Search, ArrowUp, ArrowDown, X, Download, Printer } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/Contacts.css';

interface Project {
  _id: string;
  name: string;
}

interface InspectionType {
  _id: string;
  name: string;
}

interface RelatedDeficiencyItem {
  deficiency: string;
  relationshipType: 'duplicate' | 'related';
}

interface DeficiencyFormData {
  projectId: string;
  scopeType: 'units' | 'commonArea';
  unitId?: string;
  commonAreaId?: string;
  inspectionType?: string;
  typeOfDeficiency?: string;
  locationCategory?: string;
  impactLevel: string;
  priority: string;
  dueDate: string;
  tags: string;
  title: string;
  description: string;
  attachments: File[];
  isPrivate: boolean;
  scheduleImpact?: string;
  chargeback?: string;
  costImpact?: string;
  trade?: string;
  approver?: string;
  costCodes?: string;
  privateNotes?: string;
  relatedDeficiencyItems: RelatedDeficiencyItem[];
}

interface Deficiency {
  _id: string;
  deficiencyNumber?: number;
  title: string;
  description: string;
  status: string;
  priority: string;
  priorityName: string;
  impactLevel: string;
  locationCategory: string;
  locationName: string;
  dueDate: string;
  scopeType: 'units' | 'commonArea';
  unit?: {
    _id: string;
    unitNo: string;
  };
  commonArea?: {
    _id: string;
    areaName: string;
    level: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const Deficiencies: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [loadingDeficiencies, setLoadingDeficiencies] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState<string>('deficiencyNumber');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [inspectionTypes, setInspectionTypes] = useState<InspectionType[]>([]);
  const [deficiencies, setDeficiencies] = useState<Deficiency[]>([]);

  // Filter states
  const [filters, setFilters] = useState({
    scopeType: '',
    status: '',
    priority: '',
    locationCategory: '',
    inspectionType: '',
    typeOfDeficiency: '',
    trade: '',
    tag: '',
    impactLevel: '',
    reportedBy: '',
    closedBy: '',
    completedBy: '',
    assignee: '',
    unitId: '',
    startDate: '',
    endDate: '',
    dateType: ''
  });

  // Age filter state (for display only)
  const [ageFilterDisplay, setAgeFilterDisplay] = useState<{ min?: number; max?: number } | null>(null);

  // Filter options
  const [priorities, setPriorities] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [deficiencyTypes, setDeficiencyTypes] = useState<any[]>([]);
  const [trades, setTrades] = useState<any[]>([]);
  const [tags, setTags] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [showFormSlider, setShowFormSlider] = useState(false);
  const [savingDeficiency, setSavingDeficiency] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printing, setPrinting] = useState(false);
  const isFetchingRef = useRef(false);
  const unitNoDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialUnitFilterMount = useRef(true);
  const [unitNoInput, setUnitNoInput] = useState('');
  const [unitSuggestions, setUnitSuggestions] = useState<any[]>([]);
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const unitDropdownRef = useRef<HTMLDivElement>(null);
  const urlParamsAppliedRef = useRef(false);

  // Get view state from URL params
  const viewDeficiencyId = searchParams.get('view');
  const [formData, setFormData] = useState<DeficiencyFormData>({
    projectId: '',
    scopeType: 'units',
    impactLevel: '',
    priority: '',
    dueDate: '',
    tags: '',
    title: '',
    description: '',
    attachments: [],
    isPrivate: false,
    relatedDeficiencyItems: []
  });

  useEffect(() => {
    if (workspaceId) {
      fetchProjects();
    }
  }, [workspaceId]);

  // Main URL-driven effect - Single source of truth
  useEffect(() => {
    console.log('🔄 Main useEffect triggered', {
      projectsLength: projects.length,
      urlProjectId: searchParams.get('projectId'),
      selectedProjectId,
      filterOptionsLoaded
    });

    if (projects.length === 0) {
      console.log('⏸️ Blocked: projects not loaded');
      return;
    }

    const urlProjectId = searchParams.get('projectId');

    // If no projectId in URL, don't proceed
    if (!urlProjectId || !projects.some(p => p._id === urlProjectId)) {
      console.log('❌ Blocked: no valid projectId in URL');
      return;
    }

    // Check if project changed
    const projectChanged = selectedProjectId !== urlProjectId;

    // Set selected project if different
    if (projectChanged) {
      console.log('🔄 Project changed, loading filter options...');
      setSelectedProjectId(urlProjectId);
      // Load filter options for new project, then return
      // The next cycle will fetch deficiencies after options are loaded
      fetchFilterOptions(urlProjectId); // Pass projectId since state hasn't updated yet
      return; // Don't fetch deficiencies yet
    }

    console.log('✅ Reading URL params and preparing to fetch...');

    // Read all filter params from URL (always do this to update dropdown values)
    const inspectionType = searchParams.get('inspectionType');
    const status = searchParams.get('status');
    const scopeType = searchParams.get('scopeType');
    const priority = searchParams.get('priority');
    const locationCategory = searchParams.get('locationCategory');
    const typeOfDeficiency = searchParams.get('typeOfDeficiency');
    const trade = searchParams.get('trade');
    const tag = searchParams.get('tag');
    const impactLevel = searchParams.get('impactLevel');
    const reportedBy = searchParams.get('reportedBy');
    const closedBy = searchParams.get('closedBy');
    const completedBy = searchParams.get('completedBy');
    const assignee = searchParams.get('assignee');
    const urlSortBy = searchParams.get('sortBy');
    const urlSortOrder = searchParams.get('sortOrder');
    const urlPage = searchParams.get('page');
    const urlLimit = searchParams.get('limit');
    const ageMin = searchParams.get('ageMin');
    const ageMax = searchParams.get('ageMax');

    // Build filters object from URL
    const urlFilters: any = {
      scopeType: scopeType || '',
      status: status || '',
      priority: priority || '',
      locationCategory: locationCategory || '',
      inspectionType: inspectionType || '',
      typeOfDeficiency: typeOfDeficiency || '',
      trade: trade || '',
      tag: tag || '',
      impactLevel: impactLevel || '',
      reportedBy: reportedBy || '',
      closedBy: closedBy || '',
      completedBy: completedBy || '',
      assignee: assignee || '',
      unitId: filters.unitId,
      startDate: '',
      endDate: '',
      dateType: ''
    };

    // Handle age filter if present
    if (ageMin || ageMax) {
      const now = new Date();
      let startDate = '';
      let endDate = '';

      if (ageMax) {
        const maxDate = new Date(now);
        maxDate.setDate(maxDate.getDate() - parseInt(ageMax));
        maxDate.setHours(0, 0, 0, 0);
        startDate = maxDate.toISOString();
      }

      if (ageMin) {
        const minAge = parseInt(ageMin);
        const minDate = new Date(now);
        if (minAge === 0) {
          minDate.setHours(23, 59, 59, 999);
        } else {
          minDate.setDate(minDate.getDate() - minAge);
          minDate.setHours(23, 59, 59, 999);
          minDate.setTime(minDate.getTime() - 1000);
        }
        endDate = minDate.toISOString();
      }

      urlFilters.startDate = startDate;
      urlFilters.endDate = endDate;
      urlFilters.dateType = 'createdAt';

      setAgeFilterDisplay({
        min: ageMin ? parseInt(ageMin) : undefined,
        max: ageMax ? parseInt(ageMax) : undefined
      });
    } else {
      setAgeFilterDisplay(null);
    }

    // Apply filters from URL
    setFilters(urlFilters);

    // Apply sorting from URL
    if (urlSortBy) {
      setSortBy(urlSortBy);
    }
    if (urlSortOrder && (urlSortOrder === 'asc' || urlSortOrder === 'desc')) {
      setSortOrder(urlSortOrder as 'asc' | 'desc');
    }

    // Apply pagination from URL
    const page = urlPage ? parseInt(urlPage) : 1;
    const limit = urlLimit ? parseInt(urlLimit) : 20;
    setPagination({ page, limit, total: pagination.total, pages: pagination.pages });

    // Fetch deficiencies with URL params (only if filter options are loaded)
    console.log('📊 Ready to fetch?', {
      filterOptionsLoaded,
      loading,
      isFetching: isFetchingRef.current
    });

    if (filterOptionsLoaded && !loading && !isFetchingRef.current) {
      console.log('✅ Calling fetchDeficiencies');
      fetchDeficiencies(false);
    } else {
      console.log('⏸️ Blocked from fetching:', {
        filterOptionsLoaded: !filterOptionsLoaded ? 'NOT LOADED' : 'OK',
        loading: loading ? 'LOADING' : 'OK',
        isFetching: isFetchingRef.current ? 'ALREADY FETCHING' : 'OK'
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, projects.length, filterOptionsLoaded]);


  // Fetch unit suggestions when user types
  const fetchUnitSuggestions = async (query: string) => {
    if (!selectedProjectId || !workspaceId || query.trim().length === 0) {
      setUnitSuggestions([]);
      setShowUnitDropdown(false);
      return;
    }

    try {
      const response = await api.get(`/workspaces/${workspaceId}/units`, {
        params: {
          projectId: selectedProjectId,
          search: query,
          limit: 10
        }
      });
      setUnitSuggestions(response.data.units || []);
      setShowUnitDropdown(true);
    } catch (err: any) {
      console.error('Error fetching unit suggestions:', err);
      setUnitSuggestions([]);
      setShowUnitDropdown(false);
    }
  };

  // Debounced effect for search query
  useEffect(() => {
    // Clear existing timeout
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Set new timeout - wait 500ms after user stops typing
    searchDebounceRef.current = setTimeout(() => {
      setSearchQuery(searchInput);
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500);

    // Cleanup timeout on unmount
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchInput]);

  // Debounced effect for unitNo search - fetch suggestions after user stops typing
  useEffect(() => {
    // Clear existing timeout
    if (unitNoDebounceRef.current) {
      clearTimeout(unitNoDebounceRef.current);
    }

    // Set new timeout - wait 300ms after user stops typing
    unitNoDebounceRef.current = setTimeout(() => {
      fetchUnitSuggestions(unitNoInput);
    }, 300);

    // Cleanup timeout on unmount
    return () => {
      if (unitNoDebounceRef.current) {
        clearTimeout(unitNoDebounceRef.current);
      }
    };
  }, [unitNoInput, selectedProjectId, workspaceId]);


  // Click outside handler for unit dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (unitDropdownRef.current && !unitDropdownRef.current.contains(event.target as Node)) {
        setShowUnitDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Refetch when unit filter changes (unit filter is not stored in URL params)
  useEffect(() => {
    if (isInitialUnitFilterMount.current) {
      isInitialUnitFilterMount.current = false;
      return;
    }
    if (filterOptionsLoaded && !loading && selectedProjectId && !isFetchingRef.current) {
      fetchDeficiencies(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.unitId]);

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const fetchProjects = async () => {
    if (!workspaceId) return;

    try {
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      const projectsList = response.data.projects;
      setProjects(projectsList);

      // Auto-select first project if no projectId in URL
      const urlProjectId = searchParams.get('projectId');
      if (projectsList.length > 0 && !urlProjectId) {
        // Update URL with first project (triggers main useEffect)
        const params = new URLSearchParams();
        params.set('projectId', projectsList[0]._id);
        setSearchParams(params);
      }

      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching projects:', err);
      toast.error('Failed to load projects');
      setLoading(false);
    }
  };

  const handleProjectChange = (projectId: string) => {
    // Update URL with only projectId, removing all other filters
    const params = new URLSearchParams();
    params.set('projectId', projectId);
    setSearchParams(params);
  };

  const loadInspectionTypes = async () => {
    if (!selectedProjectId) return;

    try {
      const response = await api.get(`/service-settings/${selectedProjectId}/inspection-types`);
      console.log('Inspection types response:', response.data);
      // Inspection types API returns { items: [] }
      setInspectionTypes(response.data.items || []);
    } catch (err: any) {
      console.error('Error loading inspection types:', err);
    }
  };

  const fetchFilterOptions = async (projectId?: string) => {
    const targetProjectId = projectId || selectedProjectId;
    if (!targetProjectId) return;

    try {
      console.log('🔄 fetchFilterOptions started for project:', targetProjectId);
      setFilterOptionsLoaded(false); // Mark as loading

      const [
        prioritiesRes,
        locationsRes,
        deficiencyTypesRes,
        tradesRes,
        tagsRes,
        usersRes
      ] = await Promise.all([
        api.get(`/service-settings/${targetProjectId}/priorities`),
        api.get(`/service-settings/${targetProjectId}/locations`),
        api.get(`/service-settings/${targetProjectId}/deficiency-types`),
        api.get(`/service-settings/${targetProjectId}/trades`),
        api.get(`/service-settings/${targetProjectId}/tags`),
        api.get(`/service-settings/${targetProjectId}/users`)
      ]);

      console.log('✅ All filter options loaded');

      // API returns { items: [] } for all service settings endpoints except locations
      setPriorities(prioritiesRes.data.items || []);
      setLocations(locationsRes.data.locations || []);
      setDeficiencyTypes(deficiencyTypesRes.data.items || []);
      setTrades(tradesRes.data.items || []);
      setTags(tagsRes.data.items || []);
      setUsers(usersRes.data.users || []);

      console.log('✅ Setting filterOptionsLoaded = true');
      setFilterOptionsLoaded(true); // Mark as loaded
    } catch (err: any) {
      console.error('❌ Error loading filter options:', err);
      setFilterOptionsLoaded(true); // Set to true even on error to avoid blocking
    }
  };

  useEffect(() => {
    if (selectedProjectId) {
      loadInspectionTypes();
    }
  }, [selectedProjectId]);

  const handleCreateNew = () => {
    if (!selectedProjectId) {
      toast.error('Please select a project first');
      return;
    }
    setFormData({
      projectId: selectedProjectId,
      scopeType: 'units',
      impactLevel: '',
      priority: '',
      dueDate: '',
      tags: '',
      title: '',
      description: '',
      attachments: [],
      isPrivate: false,
      relatedDeficiencyItems: []
    });
    setShowFormSlider(true);
  };

  const handleSaveDeficiency = async (data: DeficiencyFormData) => {
    try {
      setSavingDeficiency(true);

      // Upload attachments first if any
      let uploadedAttachments: any[] = [];

      if (data.attachments && data.attachments.length > 0) {
        toast.info(`Uploading ${data.attachments.length} image(s)...`);

        const uploadPromises = data.attachments.map(async (file) => {
          const formData = new FormData();
          formData.append('file', file);

          try {
            const response = await api.post(`/workspaces/${workspaceId}/upload/image`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });
            console.log('Upload response:', response.data);

            // Extract path (not URL) from the response - path is permanent, URL expires
            const imagePath = response.data.image?.path || response.data.path;

            if (!imagePath) {
              console.error('No path in upload response:', response.data);
              toast.error(`Failed to get path for ${file.name}`);
              return null;
            }

            return {
              url: imagePath, // Store path, server will convert to presigned URL when needed
              filename: file.name,
              uploadedAt: new Date()
            };
          } catch (err) {
            console.error('Error uploading file:', err);
            toast.error(`Failed to upload ${file.name}`);
            return null;
          }
        });

        const results = await Promise.all(uploadPromises);
        uploadedAttachments = results.filter(item => item !== null);

        if (uploadedAttachments.length > 0) {
          toast.success(`${uploadedAttachments.length} image(s) uploaded successfully`);
        }
      }

      // Prepare deficiency data
      const deficiencyData = {
        projectId: data.projectId,
        scopeType: data.scopeType,
        unitId: data.unitId,
        commonAreaId: data.commonAreaId,
        inspectionType: data.inspectionType,
        typeOfDeficiency: data.typeOfDeficiency,
        locationCategory: data.locationCategory,
        impactLevel: data.impactLevel,
        priority: data.priority,
        dueDate: data.dueDate,
        tags: data.tags ? data.tags.split(',').map(tag => tag.trim()) : [],
        title: data.title,
        description: data.description,
        attachments: uploadedAttachments,
        isPrivate: data.isPrivate,
        scheduleImpact: data.scheduleImpact,
        chargeback: data.chargeback,
        costImpact: data.costImpact,
        trade: data.trade,
        approver: data.approver,
        costCodes: data.costCodes,
        privateNotes: data.privateNotes,
        relatedDeficiencyItems: data.relatedDeficiencyItems
      };

      await api.post(`/workspaces/${workspaceId}/${data.projectId}/deficiencies`, deficiencyData);
      toast.success('Deficiency created successfully');
      setShowFormSlider(false);

      // Update selected project if it was changed in the form
      if (data.projectId !== selectedProjectId) {
        setSelectedProjectId(data.projectId);
      }

      // Reload deficiencies
      await fetchDeficiencies();
    } catch (err: any) {
      console.error('Error creating deficiency:', err);
      toast.error(err.response?.data?.message || 'Failed to create deficiency');
    } finally {
      setSavingDeficiency(false);
    }
  };

  const fetchDeficiencies = async (isDataRefresh: boolean = false) => {
    if (!selectedProjectId) return;

    // Prevent duplicate calls
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;

      // Use dataLoading for data refreshes (pagination, status), loadingDeficiencies for initial load
      if (isDataRefresh) {
        setDataLoading(true);
      } else {
        setLoadingDeficiencies(true);
      }

      // Read ALL parameters from URL (single source of truth)
      const params: any = {
        page: searchParams.get('page') || pagination.page,
        limit: searchParams.get('limit') || pagination.limit,
        sortBy: searchParams.get('sortBy') || sortBy,
        sortOrder: searchParams.get('sortOrder') || sortOrder
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      // Add filters from URL
      const urlStatus = searchParams.get('status');
      const urlScopeType = searchParams.get('scopeType');
      const urlPriority = searchParams.get('priority');
      const urlLocationCategory = searchParams.get('locationCategory');
      const urlInspectionType = searchParams.get('inspectionType');
      const urlTypeOfDeficiency = searchParams.get('typeOfDeficiency');
      const urlTrade = searchParams.get('trade');
      const urlTag = searchParams.get('tag');
      const urlImpactLevel = searchParams.get('impactLevel');

      if (urlScopeType) params.scopeType = urlScopeType;
      if (urlStatus) params.status = urlStatus;
      if (urlPriority) params.priority = urlPriority;
      if (urlLocationCategory) params.locationCategory = urlLocationCategory;
      if (urlInspectionType) params.inspectionType = urlInspectionType;
      if (urlTypeOfDeficiency) params.typeOfDeficiency = urlTypeOfDeficiency;
      if (urlTrade) params.trade = urlTrade;
      if (urlTag) params.tag = urlTag;
      if (urlImpactLevel) params.impactLevel = urlImpactLevel;
      for (const key of ['reportedBy', 'closedBy', 'completedBy', 'assignee']) {
        const value = searchParams.get(key);
        if (value) params[key] = value;
      }
      if (filters.unitId) params.unitId = filters.unitId; // Unit ID not in URL

      // Handle age filter from URL - calculate date range
      const ageMin = searchParams.get('ageMin');
      const ageMax = searchParams.get('ageMax');

      if (ageMin || ageMax) {
        const now = new Date();
        let startDate = '';
        let endDate = '';

        if (ageMax) {
          const maxDate = new Date(now);
          maxDate.setDate(maxDate.getDate() - parseInt(ageMax));
          maxDate.setHours(0, 0, 0, 0);
          startDate = maxDate.toISOString();
        }

        if (ageMin) {
          const minAge = parseInt(ageMin);
          const minDate = new Date(now);
          if (minAge === 0) {
            minDate.setHours(23, 59, 59, 999);
          } else {
            minDate.setDate(minDate.getDate() - minAge);
            minDate.setHours(23, 59, 59, 999);
            minDate.setTime(minDate.getTime() - 1000);
          }
          endDate = minDate.toISOString();
        }

        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        params.dateType = 'createdAt';
      }

      const response = await api.get(`/workspaces/${workspaceId}/${selectedProjectId}/deficiencies`, { params });
      setDeficiencies(response.data.deficiencies || []);

      // Only update pagination if values actually changed to avoid triggering useEffect
      const newPagination = response.data.pagination;
      if (newPagination) {
        if (
          newPagination.page !== pagination.page ||
          newPagination.limit !== pagination.limit ||
          newPagination.total !== pagination.total ||
          newPagination.pages !== pagination.pages
        ) {
          setPagination(newPagination);
        }
      }
    } catch (err: any) {
      console.error('Error fetching deficiencies:', err);
      toast.error('Failed to load deficiencies');
    } finally {
      if (isDataRefresh) {
        setDataLoading(false);
      } else {
        setLoadingDeficiencies(false);
      }
      isFetchingRef.current = false;
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    // The debounced effect will update searchQuery after 500ms
  };

  const handleFilterChange = (filterName: string, value: string) => {
    // Update URL with new filter value
    const params = new URLSearchParams(searchParams);

    if (value) {
      params.set(filterName, value);
    } else {
      params.delete(filterName);
    }

    // Reset to page 1 when filtering
    params.delete('page');

    setSearchParams(params);
  };

  const handleUnitNoChange = (value: string) => {
    // Update local input state immediately (for typing)
    setUnitNoInput(value);
    // Clear selected unit when user types
    if (selectedUnitId) {
      setSelectedUnitId('');
      setFilters(prev => ({ ...prev, unitId: '' }));
    }
  };

  const handleSelectUnit = (unit: any) => {
    setUnitNoInput(unit.unitNo);
    setSelectedUnitId(unit._id);
    setFilters(prev => ({ ...prev, unitId: unit._id }));
    setShowUnitDropdown(false);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    // Keep only projectId in URL, remove all other filters
    const params = new URLSearchParams();
    const projectId = searchParams.get('projectId');
    if (projectId) {
      params.set('projectId', projectId);
    }

    // Clear UI state
    setUnitNoInput('');
    setSelectedUnitId('');
    setShowUnitDropdown(false);
    setFilters(prev => ({ ...prev, unitId: '' }));

    setSearchParams(params);
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  const handleExport = async (selectedColumns: string[]): Promise<any[]> => {
    if (!selectedProjectId || !workspaceId) return [];

    try {
      setExporting(true);

      // Build params with current filters but no pagination limit
      const params: any = {
        limit: 10000, // Get all records
        sortBy: sortBy,
        sortOrder: sortOrder
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      // Add filters
      if (filters.scopeType) params.scopeType = filters.scopeType;
      if (filters.status) params.status = filters.status;
      if (filters.priority) params.priority = filters.priority;
      if (filters.locationCategory) params.locationCategory = filters.locationCategory;
      if (filters.inspectionType) params.inspectionType = filters.inspectionType;
      if (filters.typeOfDeficiency) params.typeOfDeficiency = filters.typeOfDeficiency;
      if (filters.trade) params.trade = filters.trade;
      if (filters.tag) params.tag = filters.tag;
      if (filters.impactLevel) params.impactLevel = filters.impactLevel;
      if (filters.reportedBy) params.reportedBy = filters.reportedBy;
      if (filters.closedBy) params.closedBy = filters.closedBy;
      if (filters.completedBy) params.completedBy = filters.completedBy;
      if (filters.assignee) params.assignee = filters.assignee;
      if (filters.unitId) params.unitId = filters.unitId;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.dateType) params.dateType = filters.dateType;

      const response = await api.get(`/workspaces/${workspaceId}/${selectedProjectId}/deficiencies`, { params });
      const allDeficiencies = response.data.deficiencies || [];

      // Get project name
      const projectName = projects.find(p => p._id === selectedProjectId)?.name || '';

      // Helper function to extract name from Mixed type object
      const extractName = (obj: any): string => {
        if (!obj) return '';
        if (typeof obj === 'string') return obj;
        if (typeof obj === 'object') {
          // Try to get name from object - could be firstName + lastName or just name
          if (obj.firstName || obj.lastName) {
            return [obj.firstName, obj.lastName].filter(Boolean).join(' ');
          }
          if (obj.name) return obj.name;
        }
        return '';
      };

      // Map deficiencies to export format - create full data object
      const fullDataRows = allDeficiencies.map((def: any) => ({
        deficiencyNo: def.deficiencyNumber || '',
        project: projectName,
        unit: def.scopeType === 'commonArea'
          ? def.commonArea?.areaName || 'Common Area'
          : def.unit?.unitNo || '-',
        status: def.status ? def.status.charAt(0).toUpperCase() + def.status.slice(1).replace('-', ' ') : '',
        priority: def.priorityName || def.priority || '',
        tags: def.tags?.join(', ') || '',
        deficiencyType: def.typeOfDeficiencyName || '',
        title: def.title || '',
        reportedOn: def.createdAt ? new Date(def.createdAt).toLocaleDateString() : '',
        reportedBy: extractName(def.createdBy),
        dueDate: def.dueDate ? new Date(def.dueDate).toLocaleDateString() : '',
        locationCategory: def.locationName || '',
        assignee: def.tradeName || '', // Assignee is the trade responsible for the deficiency
        assignedBy: '', // No assignedBy field in model - leave empty for now
        assignedDate: def.assignedDate ? new Date(def.assignedDate).toLocaleDateString() : '',
        completedBy: extractName(def.completedBy),
        completedDate: def.completedAt ? new Date(def.completedAt).toLocaleDateString() : '', // Fixed: completedAt not completedDate
        openBy: extractName(def.openedBy),
        openDate: def.openedAt ? new Date(def.openedAt).toLocaleDateString() : '', // Fixed: openedAt not openDate
        closedBy: extractName(def.closedBy),
        closedDate: def.closedAt ? new Date(def.closedAt).toLocaleDateString() : '', // Fixed: closedAt not closedDate
        updatedBy: extractName(def.updatedBy),
        updatedDate: def.updatedAt ? new Date(def.updatedAt).toLocaleDateString() : '',
        rejected: def.rejected ? 'Yes' : 'No',
        inspectionType: def.inspectionTypeName || ''
      }));

      // Filter to only include selected columns
      const exportData = fullDataRows.map((row: any) => {
        const filteredRow: any = {};
        selectedColumns.forEach(col => {
          if (col in row) {
            filteredRow[col] = row[col as keyof typeof row];
          }
        });
        return filteredRow;
      });

      toast.success(`Exporting ${exportData.length} deficiencies`);
      return exportData;
    } catch (err: any) {
      console.error('Error exporting deficiencies:', err);
      toast.error('Failed to export deficiencies');
      return [];
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = async (options: PrintOptions) => {
    if (!selectedProjectId || !workspaceId) return;

    try {
      setPrinting(true);

      // Fetch project details for header
      let projectDetails: any = null;
      let projectLogoUrl: string | null = null;
      try {
        const projectResponse = await api.get(`/workspaces/${workspaceId}/projects/${selectedProjectId}`);
        projectDetails = projectResponse.data.project; // API returns { project: {...} }

        // Fetch presigned URL for project logo if exists
        if (projectDetails?.logoId) {
          try {
            const logoUrlResponse = await api.get(`/workspaces/${workspaceId}/images/${projectDetails.logoId}/url`);
            projectLogoUrl = logoUrlResponse.data.url;
          } catch (logoErr) {
            console.error('Error fetching logo URL:', logoErr);
          }
        }
      } catch (projectErr) {
        console.error('Error fetching project details:', projectErr);
      }

      // Build params with current filters
      const params: any = {
        limit: 10000,
        sortBy: sortBy,
        sortOrder: sortOrder
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      // Add filters
      if (filters.scopeType) params.scopeType = filters.scopeType;
      if (filters.status) params.status = filters.status;
      if (filters.priority) params.priority = filters.priority;
      if (filters.locationCategory) params.locationCategory = filters.locationCategory;
      if (filters.inspectionType) params.inspectionType = filters.inspectionType;
      if (filters.typeOfDeficiency) params.typeOfDeficiency = filters.typeOfDeficiency;
      if (filters.trade) params.trade = filters.trade;
      if (filters.tag) params.tag = filters.tag;
      if (filters.impactLevel) params.impactLevel = filters.impactLevel;
      if (filters.reportedBy) params.reportedBy = filters.reportedBy;
      if (filters.closedBy) params.closedBy = filters.closedBy;
      if (filters.completedBy) params.completedBy = filters.completedBy;
      if (filters.assignee) params.assignee = filters.assignee;
      if (filters.unitId) params.unitId = filters.unitId;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.dateType) params.dateType = filters.dateType;

      const response = await api.get(`/workspaces/${workspaceId}/${selectedProjectId}/deficiencies`, { params });
      let allDeficiencies = response.data.deficiencies || [];

      // If images are to be shown, fetch presigned URLs for all attachment paths
      if (options.imageSize !== 'none') {
        // Collect all unique attachment paths
        const allPaths: string[] = [];
        allDeficiencies.forEach((def: any) => {
          if (def.attachments && def.attachments.length > 0) {
            def.attachments.forEach((att: any) => {
              if (att.url && !att.url.startsWith('http')) {
                allPaths.push(att.url);
              }
            });
          }
        });

        // Fetch presigned URLs in batch if there are paths
        if (allPaths.length > 0) {
          try {
            const urlsResponse = await api.post(`/workspaces/${workspaceId}/presigned-urls/batch`, { paths: allPaths });
            const urlMap = new Map<string, string>();
            urlsResponse.data.urls.forEach((item: { path: string; url: string }) => {
              if (item.url) {
                urlMap.set(item.path, item.url);
              }
            });

            // Update attachment URLs with presigned URLs
            allDeficiencies = allDeficiencies.map((def: any) => ({
              ...def,
              attachments: def.attachments?.map((att: any) => ({
                ...att,
                url: urlMap.get(att.url) || att.url
              }))
            }));
          } catch (urlError) {
            console.error('Error fetching presigned URLs:', urlError);
            toast.warning('Some images may not display correctly');
          }
        }
      }

      // Get image size dimensions
      const imageSizes = {
        none: 0,
        small: 80,
        medium: 150,
        large: 250
      };
      const imageWidth = imageSizes[options.imageSize];

      // Helper function to format address
      const formatAddress = (address: any): string => {
        if (!address) return '';
        const parts = [
          address.street1,
          address.street2,
          address.city,
          address.state,
          address.zip,
          address.country
        ].filter(Boolean);
        return parts.join(', ');
      };

      // Create print content
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${options.title}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              color: #333;
            }
            .project-header {
              display: flex;
              gap: 20px;
              margin-bottom: 20px;
              padding-bottom: 20px;
              border-bottom: 1px solid #ddd;
            }
            .project-logo {
              flex-shrink: 0;
            }
            .project-logo img {
              max-width: 120px;
              max-height: 80px;
              object-fit: contain;
            }
            .project-info {
              display: flex;
              gap: 40px;
              flex: 1;
            }
            .project-info-box {
              flex: 1;
            }
            .project-builder-name {
              font-size: 18px;
              font-weight: bold;
              color: #48182F;
              margin-bottom: 4px;
            }
            .project-name {
              font-size: 16px;
              font-weight: 600;
              color: #333;
              margin-bottom: 8px;
            }
            .project-address-label {
              font-size: 11px;
              color: #666;
              text-transform: uppercase;
              margin-bottom: 2px;
              margin-top: 8px;
            }
            .project-address {
              font-size: 13px;
              color: #333;
              line-height: 1.4;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #48182F;
              padding-bottom: 15px;
            }
            .header h1 {
              font-size: 24px;
              color: #48182F;
              margin-bottom: 5px;
            }
            .header h2 {
              font-size: 16px;
              color: #666;
              font-weight: normal;
            }
            .deficiency-item {
              border: 1px solid #ddd;
              border-radius: 8px;
              padding: 15px;
              margin-bottom: 15px;
              page-break-inside: avoid;
            }
            .deficiency-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 10px;
              border-bottom: 1px solid #eee;
              padding-bottom: 10px;
            }
            .deficiency-number-title {
              font-size: 16px;
              font-weight: bold;
              color: #48182F;
              flex: 1;
            }
            .deficiency-status {
              padding: 4px 12px;
              border-radius: 12px;
              font-size: 12px;
              font-weight: 500;
              flex-shrink: 0;
              margin-left: 10px;
            }
            .status-open { background: #FEF3C7; color: #92400E; }
            .status-assigned { background: #DBEAFE; color: #1E40AF; }
            .status-accepted { background: #D1FAE5; color: #065F46; }
            .status-not-accepted { background: #FEE2E2; color: #DC2626; }
            .status-completed { background: #D1FAE5; color: #065F46; }
            .status-closed { background: #E5E7EB; color: #374151; }
            .status-reopened { background: #FEF3C7; color: #92400E; }
            .deficiency-details {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 10px;
              font-size: 13px;
              margin-bottom: 10px;
            }
            .detail-item {
              display: flex;
              flex-direction: column;
            }
            .detail-label {
              color: #666;
              font-size: 11px;
              text-transform: uppercase;
            }
            .detail-value {
              color: #333;
              font-weight: 500;
            }
            .deficiency-description {
              font-size: 13px;
              color: #555;
              margin-top: 10px;
              padding-top: 10px;
              border-top: 1px solid #eee;
            }
            .deficiency-images {
              display: flex;
              flex-wrap: wrap;
              gap: 10px;
              margin-top: 10px;
            }
            .deficiency-images img {
              width: ${imageWidth}px;
              height: auto;
              border-radius: 4px;
              border: 1px solid #ddd;
            }
            .private-notes {
              margin-top: 10px;
              padding: 10px;
              background: #FFF3CD;
              border-radius: 4px;
              font-size: 12px;
            }
            .private-notes-label {
              font-weight: bold;
              color: #856404;
              margin-bottom: 5px;
            }
            .print-info {
              text-align: center;
              font-size: 11px;
              color: #999;
              margin-top: 30px;
              padding-top: 15px;
              border-top: 1px solid #eee;
            }
            @media print {
              body { padding: 0; }
              .deficiency-item { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          ${projectDetails ? `
          <div class="project-header">
            ${projectLogoUrl ? `
            <div class="project-logo">
              <img src="${projectLogoUrl}" alt="Project Logo" />
            </div>
            ` : ''}
            <div class="project-info">
              <div class="project-info-box">
                <div class="project-builder-name">${projectDetails.builderName || ''}</div>
                <div class="project-name">${projectDetails.name || ''}</div>
                ${projectDetails.projectAddress ? `
                <div class="project-address-label">Project Address</div>
                <div class="project-address">${formatAddress(projectDetails.projectAddress)}</div>
                ` : ''}
              </div>
              ${projectDetails.officeAddress ? `
              <div class="project-info-box">
                <div class="project-address-label">Office Address</div>
                <div class="project-address">${formatAddress(projectDetails.officeAddress)}</div>
              </div>
              ` : ''}
            </div>
          </div>
          ` : ''}

          <div class="header">
            <h1>${options.title}</h1>
            <h2>${options.subTitle}</h2>
          </div>

          ${allDeficiencies.map((def: any) => `
            <div class="deficiency-item">
              <div class="deficiency-header">
                <span class="deficiency-number-title">#${def.deficiencyNumber || '-'} - ${def.title || ''}</span>
                <span class="deficiency-status status-${def.status?.replace(' ', '-')}">${
                  def.status ? def.status.charAt(0).toUpperCase() + def.status.slice(1).replace('-', ' ') : ''
                }</span>
              </div>
              <div class="deficiency-details">
                <div class="detail-item">
                  <span class="detail-label">Unit/Area</span>
                  <span class="detail-value">${
                    def.scopeType === 'commonArea'
                      ? def.commonArea?.areaName || 'Common Area'
                      : def.unit?.unitNo || '-'
                  }</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Location</span>
                  <span class="detail-value">${def.locationName || '-'}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Priority</span>
                  <span class="detail-value">${def.priorityName || def.priority || '-'}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Due Date</span>
                  <span class="detail-value">${def.dueDate ? new Date(def.dueDate).toLocaleDateString() : '-'}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Reported By</span>
                  <span class="detail-value">${def.createdByName || '-'}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Assignee</span>
                  <span class="detail-value">${def.assigneeName || '-'}</span>
                </div>
              </div>
              ${def.description ? `<div class="deficiency-description">${def.description}</div>` : ''}
              ${options.imageSize !== 'none' && def.attachments && def.attachments.length > 0 ? `
                <div class="deficiency-images">
                  ${def.attachments.map((att: any) => `
                    <img src="${att.url}" alt="${att.filename || 'Attachment'}" />
                  `).join('')}
                </div>
              ` : ''}
              ${options.showPrivateNotes && def.privateNotes ? `
                <div class="private-notes">
                  <div class="private-notes-label">Private Notes:</div>
                  ${def.privateNotes}
                </div>
              ` : ''}
            </div>
          `).join('')}

          <div class="print-info">
            Printed on ${new Date().toLocaleString()} | Total: ${allDeficiencies.length} deficiencies
          </div>
        </body>
        </html>
      `;

      // Open print window
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.print();
        };
      }

      setShowPrintModal(false);
    } catch (err: any) {
      console.error('Error printing deficiencies:', err);
      toast.error('Failed to print deficiencies');
    } finally {
      setPrinting(false);
    }
  };

  const handlePageChange = (page: number) => {
    // Update URL with new page
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  const handleLimitChange = (limit: number) => {
    // Update URL with new limit and reset to page 1
    const params = new URLSearchParams(searchParams);
    params.set('limit', limit.toString());
    params.delete('page'); // Reset to page 1
    setSearchParams(params);
  };

  const handleSort = (field: string) => {
    // Update URL with new sort field and order
    const params = new URLSearchParams(searchParams);
    params.set('sortBy', field);

    if (sortBy === field) {
      // Toggle sort order if clicking the same field
      const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      params.set('sortOrder', newOrder);
    } else {
      // Set new field and default to ascending
      params.set('sortOrder', 'asc');
    }

    setSearchParams(params);
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />;
  };

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <>
        <div className="builders-container">
          {/* Page Header with Project Dropdown */}
          <div className="builders-section-header" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}>
            <div>
              <h1 className="builders-section-title">Deficiencies</h1>
              <p className="builders-section-subtitle">
                Track and manage property deficiencies
              </p>
            </div>
            <div style={{ marginTop: '4px' }}>
              <select
                className="form-input"
                value={selectedProjectId}
                onChange={(e) => handleProjectChange(e.target.value)}
                disabled={loading || projects.length === 0}
                style={{ minWidth: '280px' }}
              >
                <option value="">Select Project</option>
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="builders-loading">
              <div className="builders-loading-spinner" />
            </div>
          ) : !selectedProjectId ? (
            <div className="builders-empty-state">
              <p>Please select a project to view deficiencies</p>
            </div>
          ) : (
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
            }}>
              {/* Search and New Button */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '20px',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ position: 'relative', flex: '1', minWidth: '300px', maxWidth: '400px' }}>
                  <Search
                    size={18}
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#9CA3AF'
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Search by title, description, location"
                    value={searchInput}
                    onChange={handleSearchChange}
                    className="form-input"
                    style={{
                      width: '100%',
                      paddingLeft: '40px',
                      paddingRight: searchInput ? '40px' : '12px'
                    }}
                  />
                  {searchInput && (
                    <button
                      onClick={() => setSearchInput('')}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#9CA3AF',
                        transition: 'color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#48182F'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#9CA3AF'}
                    >
                      <X size={18} />
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    className="btn btn-secondary btn-md"
                    onClick={() => setShowPrintModal(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <Printer size={18} />
                    Print
                  </button>
                  <button
                    className="btn btn-secondary btn-md"
                    onClick={() => setShowExportModal(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <Download size={18} />
                    Export
                  </button>
                  <button className="btn btn-primary btn-md" onClick={handleCreateNew}>
                    <Plus size={20} />
                    New Deficiency
                  </button>
                </div>
              </div>

              {/* Filters Section */}
              <div style={{
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: '#F5F3F0',
                borderRadius: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <div>
                    <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#48182F', margin: 0 }}>
                      Filters
                    </h3>
                    {ageFilterDisplay && (
                      <div style={{
                        fontSize: '12px',
                        color: '#6B7280',
                        marginTop: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span style={{
                          backgroundColor: '#48182F',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '500'
                        }}>
                          Age: {ageFilterDisplay.min !== undefined ? `${ageFilterDisplay.min}` : ''}{ageFilterDisplay.min !== undefined && ageFilterDisplay.max !== undefined ? '-' : ''}{ageFilterDisplay.max !== undefined ? `${ageFilterDisplay.max}` : ''}{!ageFilterDisplay.max && ageFilterDisplay.min !== undefined ? '+' : ''} days
                        </span>
                      </div>
                    )}
                  </div>
                  {hasActiveFilters && (
                    <button
                      onClick={handleClearFilters}
                      style={{
                        fontSize: '13px',
                        color: '#48182F',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        padding: '4px 8px'
                      }}
                    >
                      Clear All
                    </button>
                  )}
                </div>

                {/* First Row */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  <select
                    className="form-input"
                    value={filters.scopeType}
                    onChange={(e) => handleFilterChange('scopeType', e.target.value)}
                    style={{ fontSize: '14px' }}
                  >
                    <option value="">Select Deficiency Place</option>
                    <option value="units">Unit</option>
                    <option value="commonArea">Common Area</option>
                  </select>

                  <select
                    className="form-input"
                    value={filters.inspectionType}
                    onChange={(e) => handleFilterChange('inspectionType', e.target.value)}
                    style={{ fontSize: '14px' }}
                  >
                    <option value="">Select Inspection Type</option>
                    {inspectionTypes.map((type) => (
                      <option key={type._id} value={type._id}>
                        {type.name}
                      </option>
                    ))}
                  </select>

                  <select
                    className="form-input"
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    style={{ fontSize: '14px' }}
                  >
                    <option value="">Select Status</option>
                    <option value="open">Open</option>
                    <option value="assigned">Assigned</option>
                    <option value="accepted">Accepted</option>
                    <option value="not-accepted">Not Accepted</option>
                    <option value="completed">Completed</option>
                    <option value="closed">Closed</option>
                    <option value="reopened">Reopened</option>
                  </select>

                  <select
                    className="form-input"
                    value={filters.tag}
                    onChange={(e) => handleFilterChange('tag', e.target.value)}
                    style={{ fontSize: '14px' }}
                  >
                    <option value="">Select Tags</option>
                    {/* Deficiencies store tag names (not IDs), so filter by name */}
                    {tags.map((tag) => (
                      <option key={tag._id} value={tag.name}>
                        {tag.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Second Row */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  <select
                    className="form-input"
                    value={filters.priority}
                    onChange={(e) => handleFilterChange('priority', e.target.value)}
                    style={{ fontSize: '14px' }}
                  >
                    <option value="">Select Priority</option>
                    {priorities.map((priority) => (
                      <option key={priority._id} value={priority._id}>
                        {priority.name}
                      </option>
                    ))}
                  </select>

                  <select
                    className="form-input"
                    value={filters.locationCategory}
                    onChange={(e) => handleFilterChange('locationCategory', e.target.value)}
                    style={{ fontSize: '14px' }}
                  >
                    <option value="">Select Location / Category</option>
                    {locations.map((location) => (
                      <option key={location._id} value={location._id}>
                        {location.name}
                      </option>
                    ))}
                  </select>

                  <select
                    className="form-input"
                    value={filters.typeOfDeficiency}
                    onChange={(e) => handleFilterChange('typeOfDeficiency', e.target.value)}
                    style={{ fontSize: '14px' }}
                  >
                    <option value="">Select Type of Deficiency</option>
                    {deficiencyTypes.map((type) => (
                      <option key={type._id} value={type._id}>
                        {type.name}
                      </option>
                    ))}
                  </select>

                  <select
                    className="form-input"
                    value={filters.trade}
                    onChange={(e) => handleFilterChange('trade', e.target.value)}
                    style={{ fontSize: '14px' }}
                  >
                    <option value="">Select Trade</option>
                    {trades.map((trade) => (
                      <option key={trade._id} value={trade._id}>
                        {trade.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Third Row */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  <select
                    className="form-input"
                    value={filters.reportedBy}
                    onChange={(e) => handleFilterChange('reportedBy', e.target.value)}
                    style={{ fontSize: '14px' }}
                  >
                    <option value="">Select Reported By</option>
                    {users.map((user) => (
                      <option key={user.contactId} value={user.contactId}>
                        {user.name}
                      </option>
                    ))}
                  </select>

                  <select
                    className="form-input"
                    value={filters.closedBy}
                    onChange={(e) => handleFilterChange('closedBy', e.target.value)}
                    style={{ fontSize: '14px' }}
                  >
                    <option value="">Select Closed By</option>
                    {users.map((user) => (
                      <option key={user.contactId} value={user.contactId}>
                        {user.name}
                      </option>
                    ))}
                  </select>

                  <select
                    className="form-input"
                    value={filters.completedBy}
                    onChange={(e) => handleFilterChange('completedBy', e.target.value)}
                    style={{ fontSize: '14px' }}
                  >
                    <option value="">Select Completed By</option>
                    {users.map((user) => (
                      <option key={user.contactId} value={user.contactId}>
                        {user.name}
                      </option>
                    ))}
                  </select>

                  <select
                    className="form-input"
                    value={filters.assignee}
                    onChange={(e) => handleFilterChange('assignee', e.target.value)}
                    style={{ fontSize: '14px' }}
                  >
                    <option value="">Select Assignee</option>
                    {users.map((user) => (
                      <option key={user.contactId} value={user.contactId}>
                        {user.name}
                      </option>
                    ))}
                  </select>

                  <div ref={unitDropdownRef} style={{ position: 'relative' }}>
                    <input
                      type="text"
                      placeholder="Type unit no"
                      value={unitNoInput}
                      onChange={(e) => handleUnitNoChange(e.target.value)}
                      className="form-input"
                      style={{ fontSize: '14px' }}
                      autoComplete="off"
                    />
                    {showUnitDropdown && unitSuggestions.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #D4CBC4',
                        borderRadius: '6px',
                        marginTop: '4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15)',
                        zIndex: 1000
                      }}>
                        {unitSuggestions.map((unit) => (
                          <div
                            key={unit._id}
                            onClick={() => handleSelectUnit(unit)}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              color: '#48182F',
                              borderBottom: '1px solid #F5F3F0',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F5F3F0'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FFFFFF'}
                          >
                            <div style={{ fontWeight: '500' }}>{unit.unitNo}</div>
                            {unit.model && (
                              <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                                {unit.model}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Deficiencies Table */}
              {(loadingDeficiencies || (dataLoading && deficiencies.length === 0)) ? (
                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ width: '80px' }}>No</th>
                        <th>Unit/ Area</th>
                        <th>Title</th>
                        <th>Inspection Type</th>
                        <th>Location</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>Due Date</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...Array(5)].map((_, index) => (
                        <tr key={index}>
                          <td>
                            <div style={{
                              height: '16px',
                              backgroundColor: '#D4CBC4',
                              borderRadius: '4px',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              width: '50px'
                            }} />
                          </td>
                          <td>
                            <div style={{
                              height: '16px',
                              backgroundColor: '#D4CBC4',
                              borderRadius: '4px',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              width: '80px'
                            }} />
                          </td>
                          <td>
                            <div style={{
                              height: '16px',
                              backgroundColor: '#D4CBC4',
                              borderRadius: '4px',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              width: '200px'
                            }} />
                          </td>
                          <td>
                            <div style={{
                              height: '16px',
                              backgroundColor: '#D4CBC4',
                              borderRadius: '4px',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              width: '120px'
                            }} />
                          </td>
                          <td>
                            <div style={{
                              height: '16px',
                              backgroundColor: '#D4CBC4',
                              borderRadius: '4px',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              width: '100px'
                            }} />
                          </td>
                          <td>
                            <div style={{
                              height: '24px',
                              backgroundColor: '#D4CBC4',
                              borderRadius: '12px',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              width: '80px'
                            }} />
                          </td>
                          <td>
                            <div style={{
                              height: '24px',
                              backgroundColor: '#D4CBC4',
                              borderRadius: '12px',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              width: '90px'
                            }} />
                          </td>
                          <td>
                            <div style={{
                              height: '16px',
                              backgroundColor: '#D4CBC4',
                              borderRadius: '4px',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              width: '100px'
                            }} />
                          </td>
                          <td>
                            <div style={{
                              height: '16px',
                              backgroundColor: '#D4CBC4',
                              borderRadius: '4px',
                              animation: 'pulse 1.5s ease-in-out infinite',
                              width: '100px'
                            }} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <style>
                    {`
                      @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.5; }
                      }
                    `}
                  </style>
                </div>
              ) : deficiencies.length === 0 ? (
                <div style={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    color: '#6B7280'
                  }}>
                    <AlertTriangle size={48} color="#D1D5DB" style={{ marginBottom: '16px' }} />
                    <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                      No deficiencies found
                    </p>
                    <p style={{ fontSize: '14px', marginBottom: '20px' }}>
                      {searchQuery ? 'Try adjusting your search' : 'Get started by reporting your first deficiency'}
                    </p>
                    <button className="btn btn-primary btn-md" onClick={handleCreateNew}>
                      <Plus size={20} />
                      New Deficiency
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="data-table-container" style={{ position: 'relative' }}>
                    {/* Loading Overlay */}
                    {dataLoading && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10
                      }}>
                        <div className="builders-loading-spinner"></div>
                      </div>
                    )}

                    <table className="data-table">
                      <thead>
                        <tr>
                          <th style={{ width: '80px' }}>
                            <button
                              onClick={() => handleSort('deficiencyNumber')}
                              className="sort-button"
                              disabled={dataLoading}
                            >
                              No
                              <span className="sort-icon">
                                {getSortIcon('deficiencyNumber')}
                              </span>
                            </button>
                          </th>
                          <th>
                            <button
                              onClick={() => handleSort('unit')}
                              className="sort-button"
                              disabled={dataLoading}
                            >
                              Unit/ Area
                              <span className="sort-icon">
                                {getSortIcon('unit')}
                              </span>
                            </button>
                          </th>
                          <th>
                            <button
                              onClick={() => handleSort('title')}
                              className="sort-button"
                              disabled={dataLoading}
                            >
                              Title
                              <span className="sort-icon">
                                {getSortIcon('title')}
                              </span>
                            </button>
                          </th>
                          <th>
                            <button
                              onClick={() => handleSort('inspectionTypeName')}
                              className="sort-button"
                              disabled={dataLoading}
                            >
                              Inspection Type
                              <span className="sort-icon">
                                {getSortIcon('inspectionTypeName')}
                              </span>
                            </button>
                          </th>
                          <th>
                            <button
                              onClick={() => handleSort('locationName')}
                              className="sort-button"
                              disabled={dataLoading}
                            >
                              Location
                              <span className="sort-icon">
                                {getSortIcon('locationName')}
                              </span>
                            </button>
                          </th>
                          <th>
                            <button
                              onClick={() => handleSort('priority')}
                              className="sort-button"
                              disabled={dataLoading}
                            >
                              Priority
                              <span className="sort-icon">
                                {getSortIcon('priority')}
                              </span>
                            </button>
                          </th>
                          <th>
                            <button
                              onClick={() => handleSort('status')}
                              className="sort-button"
                              disabled={dataLoading}
                            >
                              Status
                              <span className="sort-icon">
                                {getSortIcon('status')}
                              </span>
                            </button>
                          </th>
                          <th>
                            <button
                              onClick={() => handleSort('dueDate')}
                              className="sort-button"
                              disabled={dataLoading}
                            >
                              Due Date
                              <span className="sort-icon">
                                {getSortIcon('dueDate')}
                              </span>
                            </button>
                          </th>
                          <th>
                            <button
                              onClick={() => handleSort('createdAt')}
                              className="sort-button"
                              disabled={dataLoading}
                            >
                              Created
                              <span className="sort-icon">
                                {getSortIcon('createdAt')}
                              </span>
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody style={{ opacity: dataLoading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                        {deficiencies.map((deficiency, index) => (
                          <tr
                            key={deficiency._id}
                            onClick={() => setSearchParams({ view: deficiency._id })}
                            style={{
                              backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F5F3F0',
                              cursor: dataLoading ? 'wait' : 'pointer'
                            }}
                          >
                            <td style={{ fontWeight: '600', color: '#48182F' }}>
                              {deficiency.deficiencyNumber || '-'}
                            </td>
                            <td className="data-table-primary-cell">
                              {deficiency.scopeType === 'commonArea'
                                ? deficiency.commonArea?.areaName || 'Common Area'
                                : deficiency.unit?.unitNo || '-'}
                            </td>
                            <td className="data-table-primary-cell">
                              {deficiency.title}
                            </td>
                            <td>
                              {(deficiency as any).inspectionTypeName || '-'}
                            </td>
                            <td>
                              {deficiency.locationName || '-'}
                            </td>
                            <td>
                              <span style={{
                                display: 'inline-block',
                                padding: '4px 12px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '500',
                                backgroundColor: '#F3F4F6',
                                color: '#374151'
                              }}>
                                {deficiency.priorityName || deficiency.priority}
                              </span>
                            </td>
                            <td>
                              <span style={{
                                display: 'inline-block',
                                padding: '4px 12px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '500',
                                backgroundColor:
                                  deficiency.status === 'open' ? '#FEF3C7' :
                                  deficiency.status === 'assigned' ? '#DBEAFE' :
                                  deficiency.status === 'accepted' ? '#D1FAE5' :
                                  deficiency.status === 'not-accepted' ? '#FEE2E2' :
                                  deficiency.status === 'completed' ? '#D1FAE5' :
                                  deficiency.status === 'closed' ? '#E5E7EB' :
                                  deficiency.status === 'reopened' ? '#FEF3C7' : '#F3F4F6',
                                color:
                                  deficiency.status === 'open' ? '#92400E' :
                                  deficiency.status === 'assigned' ? '#1E40AF' :
                                  deficiency.status === 'accepted' ? '#065F46' :
                                  deficiency.status === 'not-accepted' ? '#DC2626' :
                                  deficiency.status === 'completed' ? '#065F46' :
                                  deficiency.status === 'closed' ? '#374151' :
                                  deficiency.status === 'reopened' ? '#92400E' : '#6B7280'
                              }}>
                                {deficiency.status.charAt(0).toUpperCase() + deficiency.status.slice(1).replace('-', ' ')}
                              </span>
                            </td>
                            <td className="data-table-secondary-cell">
                              {deficiency.dueDate ? new Date(deficiency.dueDate).toLocaleDateString() : '-'}
                            </td>
                            <td className="data-table-secondary-cell">
                              {new Date(deficiency.createdAt).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination and Records Per Page */}
                  <div className="contacts-pagination-controls">
                    {/* Records Per Page Selector */}
                    <div className="contacts-records-per-page">
                      <span>Show</span>
                      <select
                        value={pagination.limit}
                        onChange={(e) => handleLimitChange(Number(e.target.value))}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span>records per page (Total: {pagination.total})</span>
                    </div>

                    {/* Pagination */}
                    {pagination.pages > 1 && (
                      <div className="contacts-pagination-wrapper">
                        <Pagination
                          current={pagination.page}
                          total={pagination.pages}
                          onPageChange={handlePageChange}
                          maxWidth={400}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Deficiency Form Slider */}
        <DeficiencyFormSlider
          isOpen={showFormSlider}
          onClose={() => setShowFormSlider(false)}
          onSave={handleSaveDeficiency}
          formData={formData}
          setFormData={setFormData}
          workspaceId={workspaceId || ''}
          projects={projects}
          saving={savingDeficiency}
        />

        {viewDeficiencyId && (
          <DeficiencyDetailsSlider
            isOpen={true}
            onClose={() => setSearchParams({})}
            deficiencyId={viewDeficiencyId}
            workspaceId={workspaceId || ''}
            projectId={selectedProjectId}
            projects={projects}
            onUpdate={() => {
              fetchDeficiencies();
            }}
          />
        )}

        {/* Export Modal */}
        <ExportDeficienciesModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          onExport={handleExport}
          exporting={exporting}
        />

        {/* Print Modal */}
        <PrintDeficienciesModal
          isOpen={showPrintModal}
          onClose={() => setShowPrintModal(false)}
          onPrint={handlePrint}
          printing={printing}
        />
      </>
    </AppLayout>
  );
};

export default Deficiencies;
